import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Role } from '@/types/roles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

const roleFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(50, 'Name must be 50 characters or less'),
  description: z.string().max(200, 'Description must be 200 characters or less').optional(),
  primary_manager_role_id: z.string().nullable().optional(),
  requires_2fa: z.boolean().default(false),
});

type RoleFormValues = z.infer<typeof roleFormSchema>;

interface RoleFormProps {
  role: Role | null;
  allRoles: Role[];
  onSave: (values: Partial<Role>) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

export function RoleForm({ role, allRoles, onSave, onCancel, isLoading }: RoleFormProps) {
  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: {
      name: role?.name || '',
      description: role?.description || '',
      primary_manager_role_id: role?.primary_manager_role_id || null,
      requires_2fa: role?.requires_2fa || false,
    },
  });

  // Reset form when role prop changes (e.g., switching roles while drawer is open)
  useEffect(() => {
    form.reset({
      name: role?.name || '',
      description: role?.description || '',
      primary_manager_role_id: role?.primary_manager_role_id || null,
      requires_2fa: role?.requires_2fa || false,
    });
  }, [role?.id, form]);

  // Build a helper to detect if selecting a role as manager would create a cycle
  const wouldCreateCycle = (candidateManagerId: string): boolean => {
    if (!role?.id) return false; // New role can't create a cycle
    
    const visited = new Set<string>();
    let currentId: string | null = candidateManagerId;
    
    // Traverse up the hierarchy from the candidate
    while (currentId) {
      if (currentId === role.id) {
        // The candidate (or one of its ancestors) reports to the role we're editing
        return true;
      }
      if (visited.has(currentId)) break; // Already visited, no cycle through our role
      visited.add(currentId);
      
      const currentRole = allRoles.find(r => r.id === currentId);
      currentId = currentRole?.primary_manager_role_id ?? null;
    }
    return false;
  };

  // Filter out: the current role itself, and any role that would create a hierarchy cycle
  const availableManagerRoles = allRoles.filter(r => 
    r.id !== role?.id && !wouldCreateCycle(r.id)
  );

  const handleSubmit = async (values: RoleFormValues) => {
    await onSave({
      name: values.name,
      description: values.description || null,
      primary_manager_role_id: values.primary_manager_role_id || null,
      requires_2fa: values.requires_2fa,
    });
  };

  const isSystemRole = role?.is_system;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role Name</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  placeholder="e.g., Team Lead" 
                  disabled={isSystemRole}
                />
              </FormControl>
              <FormMessage />
              {isSystemRole && (
                <FormDescription>
                  System role names cannot be changed.
                </FormDescription>
              )}
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  {...field} 
                  placeholder="Describe the responsibilities of this role..."
                  rows={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="primary_manager_role_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reports To</FormLabel>
              <Select 
                value={field.value || 'none'} 
                onValueChange={(value) => field.onChange(value === 'none' ? null : value)}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a manager role" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="none">No direct manager</SelectItem>
                  {availableManagerRoles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                The role that manages users in this role.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Two-Factor Authentication toggle hidden until MFA enforcement is implemented */}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {role ? 'Save Changes' : 'Create Role'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
